// Measurement endpoints (13 §Endpoints, §Metric definitions): metrics, targets, observations,
// scorecards and publications. Every route is behind the `kpi` feature (ADR-0016 §9).
import { z } from "zod";
import {
  Band,
  GapPolicy,
  MetricAggregation,
  MetricCadence,
  MetricDirection,
  MetricNormalization,
  MetricSource,
  MetricUnit,
  PeriodType,
  SubjectKind,
  TeamRollup,
  TrackingMode,
  Visibility,
} from "../enums";
import {
  DecimalInput,
  FreeText,
  IsoDate,
  NonEmpty,
  PeriodKey,
  periodPairRule,
  periodRangeRule,
  Uuid,
} from "../primitives";
import {
  EventQuery,
  MetricDef,
  Observation,
  ScorecardPublication,
  TargetRule,
  TaskMetricQuery,
  TeamMetric,
} from "../rows";
import { MetricBands, MetricGate, Scorecard } from "../scorecard";
import {
  CompanyItemParams,
  CompanyParams,
  ImportResult,
  ImportUpload,
  PageQuery,
  page,
} from "./common";
import { ScorecardQuery } from "./org";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const kpi = { feature: "kpi" } as const;

/**
 * `source = event` ⇔ `event_query`; `source = formula` ⇔ `formula`; `source = task` ⇔
 * `task_query` (metric_def_source_shape_check).
 */
function sourceShape(
  m: {
    source?: string | undefined;
    event_query?: unknown;
    formula?: unknown;
    task_query?: unknown;
  },
  ctx: z.RefinementCtx,
) {
  if (m.source === undefined) return;
  if ((m.source === "event") !== (m.event_query != null))
    ctx.addIssue({ code: "custom", path: ["event_query"], message: "required iff source = event" });
  if ((m.source === "formula") !== (m.formula != null))
    ctx.addIssue({ code: "custom", path: ["formula"], message: "required iff source = formula" });
  if ((m.source === "task") !== (m.task_query != null))
    ctx.addIssue({ code: "custom", path: ["task_query"], message: "required iff source = task" });
}

const metricFields = {
  key: z
    .string()
    .max(64)
    .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)*$/, "dotted lower-case key, e.g. de.outcomes"),
  name: NonEmpty,
  description: FreeText.nullable().optional(),
  unit: MetricUnit,
  direction: MetricDirection,
  aggregation: MetricAggregation,
  normalization: MetricNormalization.nullable().optional(),
  cadence: MetricCadence.nullable().optional(),
  source: MetricSource,
  event_query: EventQuery.nullable().optional(),
  formula: z.string().max(1000).nullable().optional(),
  /** T3-TASK-ROLLUPS: what a `task` metric counts on its board. */
  task_query: TaskMetricQuery.nullable().optional(),
  visibility: Visibility.optional(),
  gap_policy: GapPolicy.optional(),
  /** AGENT-REPORTING: agent-reported values wait for review (13 §Reporting duties). */
  needs_review: z.boolean().optional(),
  /** TEAM-KPI: the metric's own warning bands; null or absent = the company's fixed bands. */
  bands: MetricBands.nullable().optional(),
  /** PLATFORM-OPERATOR: the gate spec (a gate on the operator's tracker); null = none. */
  gate: MetricGate.nullable().optional(),
};

export const MetricCreate = z
  .strictObject(metricFields)
  .superRefine(sourceShape)
  .meta({ id: "MetricCreate" });

/**
 * `key` is immutable (formulas and templates reference it). Each field is validated on its own
 * here; the source shape is a property of the merged resource, so the handler must call
 * `mergeMetricPatch` (finding #11): `{ "formula": null }` alone is a valid patch, but not on a
 * formula metric.
 */
export const MetricPatch = z
  .strictObject(metricFields)
  .omit({ key: true })
  .partial()
  .meta({ id: "MetricPatch" });
export type MetricPatch = z.infer<typeof MetricPatch>;

const metricFieldNames = Object.keys(metricFields) as (keyof typeof metricFields)[];

/**
 * Applies a validated patch to the stored definition and validates the result as a whole
 * (source ⇔ event_query/formula). Returns the merged editable fields or throws `ZodError`
 * whose paths name the patch fields, for a `validation_failed` problem.
 */
export function mergeMetricPatch(
  existing: MetricDef,
  patch: unknown,
): z.infer<typeof MetricCreate> {
  const fields = MetricPatch.parse(patch);
  const current = Object.fromEntries(metricFieldNames.map((k) => [k, existing[k]]));
  return MetricCreate.parse({ ...current, ...fields });
}

/** One metric of a seeded DE template (13 §DE scorecard catalog). */
export const MetricTemplate = z
  .object({
    key: z.string(),
    name: z.string(),
    unit: MetricUnit,
    direction: MetricDirection,
    aggregation: MetricAggregation,
    cadence: MetricCadence.nullable(),
    source: MetricSource,
    event_query: EventQuery.nullable(),
    formula: z.string().nullable(),
    /**
     * T3-TASK-ROLLUPS: a `task` template's shape (measure and the date it counts on); the board,
     * the number field and any filter are chosen when it is used. Absent on other templates.
     */
    task_shape: z
      .object({
        measure: z.enum(["count", "sum", "avg"]),
        date: z.enum(["completed_at", "due_at", "created_at", "field"]),
        filter_done: z.boolean().nullable(),
      })
      .nullable()
      .optional(),
    visibility: Visibility,
    kpi_weight: z.number(),
    goal_weight: z.number(),
    tracking_mode: TrackingMode,
    default_weekly_target: z.number().nullable(),
  })
  .meta({ id: "MetricTemplate" });

export const MetricTemplateSet = z
  .object({ template_key: z.string(), metrics: z.array(MetricTemplate) })
  .meta({ id: "MetricTemplateSet" });

export const TargetQuery = PageQuery.extend({
  metric: Uuid.optional(),
  subject_kind: SubjectKind.optional(),
  subject_id: Uuid.optional(),
});

export const TargetCreate = z
  .strictObject({
    metric_def_id: Uuid,
    subject_kind: SubjectKind,
    subject_id: Uuid,
    period_type: PeriodType,
    value: DecimalInput,
    value_max: DecimalInput.nullable().optional(),
    effective_from: IsoDate,
    effective_to: IsoDate.nullable().optional(),
  })
  .meta({ id: "TargetCreate" });

export const TargetPatch = z
  .strictObject({
    value: DecimalInput.optional(),
    value_max: DecimalInput.nullable().optional(),
    effective_from: IsoDate.optional(),
    effective_to: IsoDate.nullable().optional(),
  })
  .meta({ id: "TargetPatch" });

export const ObservationQuery = PageQuery.extend({
  metric: Uuid.optional(),
  subject_kind: SubjectKind.optional(),
  subject_id: Uuid.optional(),
  period_type: PeriodType.optional(),
  from: PeriodKey.optional().describe("Inclusive period key"),
  to: PeriodKey.optional().describe("Inclusive period key"),
})
  .superRefine(periodPairRule("from"))
  .superRefine(periodPairRule("to"))
  .superRefine(periodRangeRule);

/** Manual entry: a second value for the same period supersedes the live row. */
export const ObservationCreate = z
  .strictObject({
    metric_def_id: Uuid,
    subject_kind: SubjectKind,
    subject_id: Uuid,
    period_type: PeriodType.exclude(["round"]),
    period_key: PeriodKey,
    value: DecimalInput,
    note: z.string().max(2000).nullable().optional(),
  })
  .superRefine(periodPairRule("period_key"))
  .meta({ id: "ObservationCreate" });

export const PublishRequest = z
  .strictObject({
    subject_kind: SubjectKind,
    subject_id: Uuid,
    period_type: PeriodType,
    period_key: PeriodKey,
  })
  .superRefine(periodPairRule("period_key"))
  .meta({ id: "PublishRequest" });

export const ReviseRequest = z
  .strictObject({ reason: z.string().trim().min(10).max(2000) })
  .meta({ id: "ReviseRequest" });

/**
 * MEMBER-ENTRY (founder-approved 2026-10-21): where one of the caller's own numbers stands.
 * `late`: the last ended period has no value; `due`: the current period has none yet; `entered`:
 * it has one (a new value is a correction, which needs a reason).
 */
export const MyNumberStatus = z.enum(["late", "due", "entered"]).meta({ id: "MyNumberStatus" });

/** One metric the caller may enter on a seat it holds, at the period due by its cadence. */
export const MyNumberItem = z
  .object({
    metric_def_id: Uuid,
    key: z.string(),
    name: z.string(),
    unit: MetricUnit,
    direction: MetricDirection,
    source: z.enum(["manual", "import"]),
    cadence: MetricCadence,
    period_type: PeriodType.exclude(["round"]),
    period_key: PeriodKey,
    /** The period's last working day (Mon–Fri): "Due Fri" for a week. */
    due_on: IsoDate,
    status: MyNumberStatus,
    /** The period's live value and its row (null: none yet). */
    value: z.number().nullable(),
    observation_id: Uuid.nullable(),
    target: z.number().nullable(),
    /**
     * The metric's last periods of its cadence, current first, with their live values: the
     * period picker's choices (a period with a value takes a correction, which needs a reason).
     */
    recent: z.array(z.object({ period_key: PeriodKey, value: z.number().nullable() })).max(12),
  })
  .meta({ id: "MyNumberItem" });
export type MyNumberItem = z.infer<typeof MyNumberItem>;

export const MyNumbersSeat = z
  .object({
    seat_id: Uuid,
    title: z.string(),
    /** The holder's own name (the "Seat · Holder" label). */
    holder_name: z.string(),
    /**
     * Whether the seat's KPI health (band, score, hit/miss) is the holder's to see: the chart's
     * `kpi` audience (`settings.chart.visibility.kpi`) reaches the holder. The rows are always
     * the holder's (05 `scorecard` viewer = seat#holder).
     */
    kpi_visible: z.boolean(),
    items: z.array(MyNumberItem),
  })
  .meta({ id: "MyNumbersSeat" });

export const MyNumbers = z
  .object({
    /** The company's reporting currency (a currency metric's input and display). */
    currency: z.string().length(3),
    seats: z.array(MyNumbersSeat),
  })
  .meta({ id: "MyNumbers" });
export type MyNumbers = z.infer<typeof MyNumbers>;

export const PublicationQuery = PageQuery.extend({
  subject_kind: SubjectKind.optional(),
  subject_id: Uuid.optional(),
});

// TEAM-KPI (founder-approved 2026-10-21) --------------------------------------------------------

export const TeamMetricParams = z.object({ co: Uuid, id: Uuid, metric_id: Uuid });

/**
 * A team's target for the metric: one dated rule of the team. Its first covers the team's history;
 * a change starts with the current period of `period_type`.
 */
export const TeamTargetInput = z
  .strictObject({
    period_type: PeriodType.exclude(["round"]),
    value: DecimalInput,
    value_max: DecimalInput.nullable().optional(),
  })
  .meta({ id: "TeamTargetInput" });

/**
 * One KPI of a team, written on its own (the KPI assignment dialog's "Assign to: Team").
 * `target` sets the team's target (a `target_rule` with `subject_kind = team`, dated as
 * `TeamTargetInput` says; the previous team rule ends the day before); null clears the team's
 * own rules (the company rule, if any, applies); absent leaves them.
 */
export const TeamMetricPut = z
  .strictObject({
    rollup: TeamRollup,
    kpi_weight: DecimalInput,
    position: z.int().min(0).optional(),
    target: TeamTargetInput.nullable().optional(),
  })
  .meta({ id: "TeamMetricPut" });
export type TeamMetricPut = z.infer<typeof TeamMetricPut>;

/** A team KPI with its metric's name, unit, source and bands and the team's current target. */
export const TeamMetricLine = TeamMetric.extend({
  metric_key: z.string(),
  metric_name: z.string(),
  unit: MetricUnit,
  direction: MetricDirection,
  source: MetricSource,
  cadence: MetricCadence.nullable(),
  bands: MetricBands.nullable(),
  /** The team's own rule in force today, or null (the company rule, if any, applies). */
  target: z
    .object({
      period_type: PeriodType,
      value: z.number(),
      value_max: z.number().nullable(),
      effective_from: IsoDate,
    })
    .nullable(),
}).meta({ id: "TeamMetricLine" });
export type TeamMetricLine = z.infer<typeof TeamMetricLine>;

/**
 * The team's KPI set as its viewer reads it, and whether the caller may change it (05: a team's
 * lead seat holder or a company manager writes; its members read).
 */
export const TeamMetricSet = z
  .object({ data: z.array(TeamMetricLine), can_edit: z.boolean() })
  .meta({ id: "TeamMetricSet" });
export type TeamMetricSet = z.infer<typeof TeamMetricSet>;

/** One team's score for a period: the chart's team chip and Cadence's team scorecard rows. */
export const TeamScoreSummary = z
  .object({
    team_id: Uuid,
    name: z.string(),
    kpi_score: z.number().nullable(),
    goal_score: z.number().nullable(),
    completeness: z.number(),
    band: Band,
    /** KPIs on the team's scorecard, and of those, the lines in each health. */
    metrics: z.int().min(0),
    health: z.object({ green: z.int().min(0), amber: z.int().min(0), red: z.int().min(0) }),
  })
  .meta({ id: "TeamScoreSummary" });
export type TeamScoreSummary = z.infer<typeof TeamScoreSummary>;

export const measurementRoutes = {
  "metrics.list": defineRoute({
    ...kpi,
    id: "metrics.list",
    method: "get",
    path: `${base}/metrics`,
    tag: "metrics",
    summary: "List metric definitions (agency-visibility metrics hidden from client viewers)",
    access: "reader",
    params: CompanyParams,
    query: PageQuery,
    response: { status: 200, schema: page(MetricDef) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "metrics.create": defineRoute({
    ...kpi,
    id: "metrics.create",
    method: "post",
    path: `${base}/metrics`,
    tag: "metrics",
    summary: "Define a metric",
    access: "manager",
    params: CompanyParams,
    body: MetricCreate,
    idempotency: "optional",
    response: { status: 201, schema: MetricDef },
    errors: ["conflict"],
    emits: ["metric_def.created"],
  }),
  "metrics.templates": defineRoute({
    ...kpi,
    id: "metrics.templates",
    method: "get",
    path: `${base}/metrics/templates`,
    tag: "metrics",
    summary: "The seeded metric catalog per DE template (read-only)",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: z.object({ data: z.array(MetricTemplateSet) }) },
    errors: [],
    emits: [],
  }),
  "metrics.get": defineRoute({
    ...kpi,
    id: "metrics.get",
    method: "get",
    path: `${base}/metrics/{id}`,
    tag: "metrics",
    summary: "Get a metric definition",
    access: "reader",
    params: CompanyItemParams,
    response: { status: 200, schema: MetricDef },
    errors: [],
    emits: [],
  }),
  "metrics.update": defineRoute({
    ...kpi,
    id: "metrics.update",
    method: "patch",
    path: `${base}/metrics/{id}`,
    tag: "metrics",
    summary: "Update a metric definition",
    access: "manager",
    params: CompanyItemParams,
    body: MetricPatch,
    response: { status: 200, schema: MetricDef },
    errors: [],
    emits: ["metric_def.updated"],
  }),
  "metrics.archive": defineRoute({
    ...kpi,
    id: "metrics.archive",
    method: "post",
    path: `${base}/metrics/{id}/archive`,
    tag: "metrics",
    summary: "Archive a metric definition",
    access: "manager",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: MetricDef },
    errors: [],
    emits: ["metric_def.archived"],
  }),
  // ACTIONS: an archived metric live again (409 when it is not archived).
  "metrics.unarchive": defineRoute({
    ...kpi,
    id: "metrics.unarchive",
    method: "post",
    path: `${base}/metrics/{id}/unarchive`,
    tag: "metrics",
    summary: "Restore an archived metric definition",
    access: "manager",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: MetricDef },
    errors: ["conflict"],
    emits: ["metric_def.unarchived"],
  }),

  "targets.list": defineRoute({
    ...kpi,
    id: "targets.list",
    method: "get",
    path: `${base}/targets`,
    tag: "targets",
    summary: "List target rules",
    access: "reader",
    params: CompanyParams,
    query: TargetQuery,
    response: { status: 200, schema: page(TargetRule) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "targets.create": defineRoute({
    ...kpi,
    id: "targets.create",
    method: "post",
    path: `${base}/targets`,
    tag: "targets",
    summary: "Create a target rule",
    access: "manager",
    params: CompanyParams,
    body: TargetCreate,
    idempotency: "optional",
    response: { status: 201, schema: TargetRule },
    errors: [],
    emits: ["target_rule.changed"],
  }),
  "targets.update": defineRoute({
    ...kpi,
    id: "targets.update",
    method: "patch",
    path: `${base}/targets/{id}`,
    tag: "targets",
    summary: "Update a target rule",
    access: "manager",
    params: CompanyItemParams,
    body: TargetPatch,
    response: { status: 200, schema: TargetRule },
    errors: [],
    emits: ["target_rule.changed"],
  }),
  "targets.delete": defineRoute({
    ...kpi,
    id: "targets.delete",
    method: "delete",
    path: `${base}/targets/{id}`,
    tag: "targets",
    summary: "Delete a target rule",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 204 },
    errors: [],
    emits: ["target_rule.changed"],
  }),

  "observations.list": defineRoute({
    ...kpi,
    id: "observations.list",
    method: "get",
    path: `${base}/observations`,
    tag: "observations",
    summary: "List observations (all rows, including superseded and tombstones)",
    access: "reader",
    params: CompanyParams,
    query: ObservationQuery,
    response: { status: 200, schema: page(Observation) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "observations.create": defineRoute({
    ...kpi,
    id: "observations.create",
    method: "post",
    path: `${base}/observations`,
    tag: "observations",
    summary:
      "Record a manual value (recordObservation); a seat's person holder for its own seats' manual/import metrics (MEMBER-ENTRY)",
    access: "member",
    params: CompanyParams,
    body: ObservationCreate,
    idempotency: "optional",
    response: { status: 201, schema: Observation },
    // AGENT-REPORTING: a seat duty with `evidence_required` needs a note (13 §Reporting duties)
    errors: ["metric_is_derived", "source_event_conflict", "conflict", "evidence_required"],
    emits: ["observation.recorded"],
  }),
  "me.numbers": defineRoute({
    ...kpi,
    id: "me.numbers",
    method: "get",
    path: `${base}/me/numbers`,
    tag: "observations",
    summary:
      "The caller's own numbers: per seat it holds, the metrics it may enter and their due period",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: MyNumbers },
    errors: [],
    emits: [],
  }),
  "observations.delete": defineRoute({
    ...kpi,
    id: "observations.delete",
    method: "delete",
    path: `${base}/observations/{id}`,
    tag: "observations",
    summary: "Tombstone a value; the previous live row, if any, is live again",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: Observation },
    errors: ["metric_is_derived", "conflict"],
    emits: ["observation.recorded"],
  }),
  "observations.import": defineRoute({
    ...kpi,
    id: "observations.import",
    method: "post",
    path: `${base}/observations/import`,
    tag: "imports",
    summary:
      "CSV import of observations (dry_run supported); a seat's person holder imports its own seats' rows only (MEMBER-ENTRY)",
    access: "member",
    params: CompanyParams,
    body: ImportUpload,
    bodyType: "multipart",
    idempotency: "optional",
    response: { status: 200, schema: ImportResult },
    errors: ["metric_is_derived", "payload_too_large"],
    emits: ["observation.recorded", "import.committed"],
  }),

  "scorecards.company": defineRoute({
    ...kpi,
    id: "scorecards.company",
    method: "get",
    path: `${base}/scorecards/company`,
    tag: "scorecards",
    summary: "Company roll-up scorecard (children = DE seats)",
    access: "reader",
    params: CompanyParams,
    query: ScorecardQuery,
    response: { status: 200, schema: Scorecard },
    errors: [],
    emits: [],
  }),
  "scorecards.publish": defineRoute({
    ...kpi,
    id: "scorecards.publish",
    method: "post",
    path: `${base}/scorecards/publish`,
    tag: "scorecards",
    summary: "Publish revision 1 of a period (frozen, with config_snapshot)",
    access: "manager",
    params: CompanyParams,
    body: PublishRequest,
    idempotency: "optional",
    response: { status: 201, schema: ScorecardPublication },
    errors: ["already_published"],
    emits: ["scorecard.published"],
  }),
  "scorecards.revise": defineRoute({
    ...kpi,
    id: "scorecards.revise",
    method: "post",
    path: `${base}/scorecards/publications/{id}/revise`,
    tag: "scorecards",
    summary: "Append revision N+1, scored under the stored config_snapshot",
    access: "manager",
    params: CompanyItemParams,
    body: ReviseRequest,
    idempotency: "optional",
    response: { status: 201, schema: ScorecardPublication },
    errors: ["conflict"],
    emits: ["scorecard.published"],
  }),
  "scorecards.publications": defineRoute({
    ...kpi,
    id: "scorecards.publications",
    method: "get",
    path: `${base}/scorecards/publications`,
    tag: "scorecards",
    summary: "All publication revisions, newest first",
    access: "seat_viewer",
    params: CompanyParams,
    query: PublicationQuery,
    response: { status: 200, schema: page(ScorecardPublication) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  // TEAM-KPI (founder-approved 2026-10-21): team KPIs. Reads: the team's viewers (05
  // `team#viewer`: managers, its lead, its human owner, its member seats' holders); writes: the
  // team's lead seat holder or a company manager.
  "teams.metrics": defineRoute({
    ...kpi,
    id: "teams.metrics",
    method: "get",
    path: `${base}/teams/{id}/metrics`,
    tag: "team-kpis",
    summary: "The team's KPIs: metric, roll-up, weight and the team's target",
    access: "viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: TeamMetricSet },
    errors: [],
    emits: [],
  }),
  "teams.putMetric": defineRoute({
    ...kpi,
    id: "teams.putMetric",
    method: "put",
    path: `${base}/teams/{id}/metrics/{metric_id}`,
    tag: "team-kpis",
    summary: "Assign a metric to the team or change its roll-up, weight or target",
    access: "member",
    params: TeamMetricParams,
    body: TeamMetricPut,
    response: { status: 200, schema: TeamMetricLine },
    errors: [],
    emits: ["team_metric.changed", "target_rule.changed"],
  }),
  "teams.removeMetric": defineRoute({
    ...kpi,
    id: "teams.removeMetric",
    method: "delete",
    path: `${base}/teams/{id}/metrics/{metric_id}`,
    tag: "team-kpis",
    summary: "Take a metric off the team (idempotent; its observations and targets stay)",
    access: "member",
    params: TeamMetricParams,
    response: { status: 204 },
    errors: [],
    emits: ["team_metric.changed"],
  }),
  "teams.scorecard": defineRoute({
    ...kpi,
    id: "teams.scorecard",
    method: "get",
    path: `${base}/teams/{id}/scorecard`,
    tag: "team-kpis",
    summary: "The team's scorecard for a period (computed on read; the team's viewers)",
    access: "viewer",
    params: CompanyItemParams,
    query: ScorecardQuery,
    response: { status: 200, schema: Scorecard },
    errors: [],
    emits: [],
  }),
  "scorecards.teams": defineRoute({
    ...kpi,
    id: "scorecards.teams",
    method: "get",
    path: `${base}/scorecards/teams`,
    tag: "team-kpis",
    summary: "Every team the caller may view that has KPIs, with its score for a period",
    access: "viewer",
    params: CompanyParams,
    query: ScorecardQuery,
    response: { status: 200, schema: z.object({ data: z.array(TeamScoreSummary) }) },
    errors: [],
    emits: [],
  }),
} as const;
